import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, CheckCircle2, XCircle, Clock, Link2, Key, Save, Zap, AlertTriangle,
  Send, ArrowRight, Play, Plug,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_LABELS = {
  lead: { label: "Lead (questionario)", color: "bg-blue-50 text-blue-700 border-blue-200" },
  pagamento: { label: "Pagamento Nexi", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  prenotazione: { label: "Prenotazione", color: "bg-purple-50 text-purple-700 border-purple-200" },
  pratica_nlt: { label: "Pratica NLT", color: "bg-amber-50 text-amber-700 border-amber-200" },
  test: { label: "Test ping", color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};
const STATUS_STYLES = {
  success: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Inviato" },
  failed: { icon: XCircle, cls: "text-rose-600 bg-rose-50 border-rose-200", label: "Fallito" },
  skipped: { icon: AlertTriangle, cls: "text-amber-600 bg-amber-50 border-amber-200", label: "Saltato" },
  pending: { icon: Clock, cls: "text-slate-600 bg-slate-50 border-slate-200", label: "In attesa" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function PolizzaAISyncTab() {
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ success: 0, failed: 0, skipped: 0 });
  const [filter, setFilter] = useState("all");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadCfg = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/polizzaai/config`, { withCredentials: true });
      setCfg(data);
      setBaseUrl(data.base_url || "");
      setEnabled(!!data.enabled);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore caricamento config");
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const q = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await axios.get(`${API}/polizzaai/logs${q}`, { withCredentials: true });
      setLogs(data.logs || []);
      setStats(data.stats || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore log");
    }
  }, [filter]);

  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const save = async () => {
    setErr(""); setMsg(""); setSaving(true);
    try {
      const body = { base_url: baseUrl, enabled };
      if (apiKey) body.api_key = apiKey;
      await axios.post(`${API}/polizzaai/config`, body, { withCredentials: true });
      setMsg("Configurazione salvata");
      setApiKey("");
      await loadCfg();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3500);
    }
  };

  const doTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/polizzaai/test`, {}, { withCredentials: true });
      setTestResult(data);
      await loadLogs();
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Errore" });
    } finally {
      setTesting(false);
    }
  };

  const doRetry = async (id) => {
    try {
      await axios.post(`${API}/polizzaai/retry/${id}`, {}, { withCredentials: true });
      await loadLogs();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Retry fallito");
    }
  };

  const doBulkSync = async () => {
    if (!window.confirm("Sincronizzare tutti i lead esistenti verso PolizzaAI? Può richiedere alcuni minuti.")) return;
    setBulkRunning(true); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/polizzaai/sync/submissions`, {}, { withCredentials: true });
      setMsg(`${data.scheduled_count} lead pianificati. Controlla i log tra qualche secondo.`);
      setTimeout(loadLogs, 5000);
      setTimeout(loadLogs, 15000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sync massivo fallito");
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="polizzaai-tab">
      {/* Header + stats */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-5">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
              <Plug className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-bold text-lg">Federazione PolizzaAI</h2>
              <p className="text-xs text-zinc-500 max-w-xl">
                Ogni lead, pagamento e prenotazione ricevuti sul sito pubblico viene automaticamente
                inviato al CRM PolizzaAI tramite webhook firmato HMAC-SHA256. Non blocca mai il flusso
                pubblico: se PolizzaAI è offline, l'evento resta in DB per retry.
              </p>
            </div>
          </div>
          <div className={
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border " +
            (cfg?.configured && cfg?.enabled
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-zinc-50 text-zinc-600 border-zinc-200")
          }>
            <span className={
              "w-2 h-2 rounded-full " +
              (cfg?.configured && cfg?.enabled ? "bg-emerald-500 animate-pulse" : "bg-zinc-400")
            } />
            {cfg?.configured && cfg?.enabled ? "Attivo" : cfg?.configured ? "Configurato (disabilitato)" : "Non configurato"}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Inviati OK" value={stats.success || 0} tone="emerald" icon={CheckCircle2} />
          <Stat label="Falliti" value={stats.failed || 0} tone="rose" icon={XCircle} />
          <Stat label="Saltati" value={stats.skipped || 0} tone="amber" icon={AlertTriangle} />
        </div>
      </div>

      {/* Config */}
      <div className="bg-white rounded-2xl border border-zinc-200 p-5">
        <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
          <Link2 className="w-4 h-4 text-zinc-500" />
          Configurazione webhook
        </h3>
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-zinc-700 mb-1.5 block">URL base PolizzaAI</span>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://main-branch-novo.preview.emergentagent.com"
              className="w-full border border-zinc-200 focus:border-blue-400 rounded-lg px-3 py-2 text-sm outline-none font-mono"
              data-testid="polizzaai-url-input"
            />
            <p className="text-[11px] text-zinc-400 mt-1">
              Senza trailing slash. Eventi inviati a <code className="bg-zinc-100 px-1 rounded">&lt;url&gt;/api/webhook/colucci/&lt;event_type&gt;</code>
            </p>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-zinc-700 mb-1.5 block flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5" />
              API Key (shared secret)
            </span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg?.api_key_set ? `Attuale: ${cfg.api_key_masked} (lascia vuoto per non cambiare)` : "Incolla qui la chiave segreta"}
              className="w-full border border-zinc-200 focus:border-blue-400 rounded-lg px-3 py-2 text-sm outline-none font-mono"
              data-testid="polizzaai-key-input"
              autoComplete="new-password"
            />
            <p className="text-[11px] text-zinc-400 mt-1">
              Usata per firmare HMAC-SHA256 ogni payload. Inserisci la stessa chiave configurata lato PolizzaAI.
            </p>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 rounded accent-blue-600"
              data-testid="polizzaai-enable-toggle"
            />
            <span className="text-sm font-medium text-zinc-700">Abilita sincronizzazione automatica</span>
            <span className="text-[11px] text-zinc-400">(se disattivato, gli eventi sono "saltati")</span>
          </label>

          <div className="flex items-center gap-2 pt-2 border-t border-zinc-100 flex-wrap">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-60"
              data-testid="polizzaai-save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Salvo…" : "Salva configurazione"}
            </button>
            <button
              onClick={doTest}
              disabled={testing || !cfg?.configured}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-medium disabled:opacity-60"
              data-testid="polizzaai-test-btn"
            >
              <Zap className="w-4 h-4" />
              {testing ? "Test…" : "Ping di test"}
            </button>
            <button
              onClick={doBulkSync}
              disabled={bulkRunning || !cfg?.configured || !cfg?.enabled}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 text-sm font-medium disabled:opacity-60"
              data-testid="polizzaai-bulk-btn"
              title="Invia a PolizzaAI tutti i lead già esistenti in DB"
            >
              <Play className="w-4 h-4" />
              {bulkRunning ? "Avvio…" : "Sync retroattivo lead"}
            </button>
          </div>
          {msg && <p className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{msg}</p>}
          {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</p>}
          {testResult && (
            <div className={
              "rounded-lg border p-3 text-xs " +
              (testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-rose-50 border-rose-200 text-rose-800")
            }>
              <p className="font-semibold mb-1">
                {testResult.ok ? "✓ Ping riuscito" : "✗ Ping fallito"}
                {testResult.http_code && ` (HTTP ${testResult.http_code})`}
              </p>
              {testResult.url && <p className="font-mono text-[11px] break-all">{testResult.url}</p>}
              {testResult.error && <p className="mt-1">{testResult.error}</p>}
              {testResult.response && (
                <details className="mt-1">
                  <summary className="cursor-pointer">Risposta</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all">{testResult.response}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Logs */}
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-zinc-500" />
            Log eventi sincronizzati
          </h3>
          <div className="flex items-center gap-2">
            {["all", "success", "failed", "skipped"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={
                  "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
                  (filter === f ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200")
                }
              >
                {f === "all" ? "Tutti" : f === "success" ? "OK" : f === "failed" ? "Falliti" : "Saltati"}
              </button>
            ))}
            <button onClick={loadLogs} className="p-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-50" title="Aggiorna">
              <RefreshCw className="w-3.5 h-3.5 text-zinc-500" />
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase text-zinc-500">
              <tr>
                <th className="px-4 py-2 text-left">Data</th>
                <th className="px-4 py-2 text-left">Tipo</th>
                <th className="px-4 py-2 text-left">Stato</th>
                <th className="px-4 py-2 text-left">HTTP</th>
                <th className="px-4 py-2 text-left">Errore / Risposta</th>
                <th className="px-4 py-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-zinc-400 text-sm">Nessun evento</td></tr>
              ) : logs.map((l) => {
                const st = STATUS_STYLES[l.status] || STATUS_STYLES.pending;
                const Ico = st.icon;
                const ev = EVENT_LABELS[l.event_type] || { label: l.event_type, color: "bg-zinc-50 text-zinc-600 border-zinc-200" };
                return (
                  <tr key={l.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                    <td className="px-4 py-2 text-xs text-zinc-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-4 py-2">
                      <span className={"inline-block px-2 py-0.5 rounded-md text-[11px] font-medium border " + ev.color}>{ev.label}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border " + st.cls}>
                        <Ico className="w-3 h-3" />{st.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-zinc-500">{l.http_code || "—"}</td>
                    <td className="px-4 py-2 text-xs text-zinc-600 max-w-[280px] truncate" title={l.error || l.response_snippet || ""}>
                      {l.error || (l.response_snippet ? l.response_snippet.substring(0, 80) : "—")}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {l.status === "failed" && (
                        <button
                          onClick={() => doRetry(l.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                          title="Riprova invio"
                        >
                          <ArrowRight className="w-3 h-3" />Retry
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Documentazione per altro agente */}
      <details className="bg-zinc-50 rounded-2xl border border-zinc-200 p-4">
        <summary className="cursor-pointer font-semibold text-sm text-zinc-700">
          📘 Cosa deve implementare PolizzaAI per ricevere gli eventi
        </summary>
        <div className="mt-3 space-y-2 text-xs text-zinc-600 leading-relaxed">
          <p>Lato PolizzaAI serve un endpoint webhook per ogni event type:</p>
          <pre className="bg-white border border-zinc-200 rounded p-2 overflow-x-auto text-[11px]">
{`POST /api/webhook/colucci/lead          ← nuovo lead da questionario
POST /api/webhook/colucci/pagamento     ← pagamento Nexi andato a buon fine
POST /api/webhook/colucci/prenotazione  ← consulenza prenotata
POST /api/webhook/colucci/pratica_nlt   ← pratica noleggio
POST /api/webhook/colucci/test          ← ping di verifica`}
          </pre>
          <p>Header inviati con ogni richiesta:</p>
          <pre className="bg-white border border-zinc-200 rounded p-2 overflow-x-auto text-[11px]">
{`Content-Type: application/json
X-Colucci-Event: <event_type>
X-Colucci-Event-Id: <uuid>
X-Colucci-Signature: <hex HMAC-SHA256 del body con shared api_key>`}
          </pre>
          <p>Payload JSON standard:</p>
          <pre className="bg-white border border-zinc-200 rounded p-2 overflow-x-auto text-[11px]">
{`{
  "event_id": "uuid",
  "event_type": "lead|pagamento|prenotazione|pratica_nlt|test",
  "ts": "ISO-8601",
  "source": "colucci-site",
  "data": { ... mappato in base all'event_type ... }
}`}
          </pre>
          <p>
            PolizzaAI deve: (1) verificare <code>X-Colucci-Signature</code>, (2) idempotenza tramite <code>event_id</code>,
            (3) mappare <code>data.cliente</code> sull'anagrafica (upsert by email), (4) rispondere <code>200 OK</code>.
          </p>
        </div>
      </details>
    </div>
  );
}

function Stat({ label, value, tone, icon: Icon }) {
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <div className={"rounded-xl border px-4 py-3 flex items-center gap-3 " + (toneMap[tone] || "bg-zinc-50")}>
      <Icon className="w-5 h-5 opacity-70" />
      <div>
        <p className="text-[11px] uppercase tracking-wider opacity-70">{label}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
    </div>
  );
}
